import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getUserPublicId } from '@infra/auth';
import { EMAIL_VERIFICATION_QUERY_KEY, isJwtAdmin, usersApi } from '@services';

/** The status exists only for a signed-in local user (not the JWT super admin). */
export function canReadEmailVerification(): boolean {
  return !isJwtAdmin() && Boolean(getUserPublicId());
}

export function useEmailVerification() {
  return useQuery({
    queryKey: EMAIL_VERIFICATION_QUERY_KEY,
    queryFn: () => usersApi.getEmailVerification(),
    enabled: canReadEmailVerification(),
    retryOnMount: false,
    // Another session may have verified or sent a code.
    refetchOnWindowFocus: true,
  });
}

/** Queries never go stale on their own, so every outcome re-reads the status. */
export function invalidateEmailVerification(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: EMAIL_VERIFICATION_QUERY_KEY });
}

export function useRequestEmailVerificationCode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => usersApi.requestEmailVerificationCode(),
    onSettled: () => invalidateEmailVerification(client),
  });
}

export function useVerifyEmailCode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => usersApi.verifyEmailCode(code),
    onSettled: () => invalidateEmailVerification(client),
  });
}
